import { LegalLayout } from "@/components/LegalLayout";

const FAQ = () => (
  <LegalLayout
    title="FAQ"
    description="Frequently asked questions about Cash Isle."
    updated="May 26, 2026"
  >
    <h2>Getting Started</h2>
    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>What is Cash Isle?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Cash Isle is an all-in-one platform for creators, freelancers, and merchants to send and receive money, find gigs, manage disputes, and grow their hustle — all in one place.
      </p>
    </details>

    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>How do I create an account?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Tap "Get Started" on the landing page, enter your email and phone number, verify via OTP, and set up your cashtag (your unique username). It takes under two minutes.
      </p>
    </details>

    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>What is a cashtag?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Your cashtag is your unique Cash Isle username (e.g., <code className="text-sm bg-muted px-1 rounded">$johndoe</code>). Others can use it to send you money or visit your profile.
      </p>
    </details>

    <h2>Wallet & Payments</h2>
    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>How do I add money to my wallet?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Go to your Wallet, tap "Add Funds," choose your payment method, and follow the prompts. Supported methods vary by region.
      </p>
    </details>

    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>How do I send money?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Tap "Send" from the Wallet, enter a cashtag, phone number, or scan a QR code, enter the amount, confirm with your PIN, and you're done.
      </p>
    </details>

    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>How do I withdraw funds?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Go to "Withdraw" in the Wallet, link a bank account or mobile money wallet, enter the amount, and confirm. Withdrawals typically process within 1–3 business days.
      </p>
    </details>

    <h2>Gigs & Creator Mode</h2>
    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>How do I post a gig?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Navigate to "Gigs," tap "Post a Gig," fill in the title, description, budget, and deadline, then publish. Interested applicants can apply directly.
      </p>
    </details>

    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>What is Creator Mode?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Creator Mode unlocks tools for creators: fan circles, quests, events, hunts, AI assistant, and monetization features. Enable it from your profile settings.
      </p>
    </details>

    <h2>Verification & Security</h2>
    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>Why do I need KYC verification?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        KYC (Know Your Customer) verification is required by law to prevent fraud and money laundering. It also unlocks higher transaction limits and withdrawal access.
      </p>
    </details>

    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>How do I complete KYC?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Go to "Verification" in settings, upload a government-issued ID and a selfie. Our verification partner Sumsub will review your submission, usually within minutes.
      </p>
    </details>

    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>Is my data secure?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Yes. We use end-to-end encryption for transactions, secure storage for documents, and comply with PCI-DSS and GDPR standards. We never sell your data.
      </p>
    </details>

    <h2>Account & Settings</h2>
    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>How do I change my PIN?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Go to "Settings &gt; Security &gt; Change PIN." You'll need your current PIN to set a new one.
      </p>
    </details>

    <details className="group mb-4">
      <summary className="cursor-pointer font-semibold text-foreground list-none flex items-center justify-between py-2 border-b border-border">
        <span>Can I close my account?</span>
        <span className="transition-transform group-open:rotate-180 text-muted-foreground">&#9662;</span>
      </summary>
      <p className="mt-3 text-foreground/90">
        Yes. Contact support with your cashtag and reason. We'll process your closure request after verifying your identity and settling any pending balances.
      </p>
    </details>

    <p className="text-xs text-muted-foreground mt-8">
      Still have questions? Visit our{" "}
      <a href="/support">Support</a> page or email{" "}
      <a href="mailto:support@cashisle.app">support@cashisle.app</a>.
    </p>
  </LegalLayout>
);

export default FAQ;
